import { Error as ErrorMessage, Logo, Spacer } from "@convergence/components";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useLogin, useRequestOtp } from "../api/useLoginOtp";
import { useSignupAnon } from "../api/useSignupAnon";
import { useTechnobabble } from "../hooks/useTechnobabble";
import { Turnstile } from "../components/Turnstile";
import { redirectUrl } from "../auth-client";

export default function LoginOtp() {
  const [showTurnstile] = useState(false);
  const {
    mutateAsync: requestOtp,
    status,
    error: otpError,
  } = useRequestOtp();
  const { signupAnon, error, isPending } = useSignupAnon();
  const loginMutation = useLogin();
  const otpSent = status !== "idle";

  useEffect(() => {
    if (error) {
      toast.error(JSON.parse(error.message).message);
    }
  }, [error]);

  const [searchParams] = useSearchParams();

  const { register, handleSubmit, formState } = useForm({
    defaultValues: { email: "", otp: "" },
  });

  const tb = useTechnobabble(2000);

  return (
    <div className="w-screen h-screen flex flex-col font-body">
      <Header />
      <main className="w-full bg-white h-full flex-grow grid place-items-center">
        <form
          className="bg-white border-black border p-8 flex flex-col items-center gap-4"
          onSubmit={handleSubmit(async ({ email, otp }) => {
            if (!otpSent) {
              requestOtp(email);
            } else {
              const result = await loginMutation.mutateAsync({ email, otp });
              // Signing in during an OAuth authorize (e.g. connecting ChatGPT) returns
              // the URL that continues that flow.
              const oauthContinue = redirectUrl(result);
              const redirect = searchParams.get("redirect");
              if (oauthContinue) {
                window.location.href = oauthContinue;
              } else if (redirect) {
                window.location.href = redirect;
              } else {
                window.location.href =
                  import.meta.env.VITE_TINKER_URL + "/app";
              }
            }
          })}
        >
          <h2 className="text-4xl mb-8">Login</h2>
          <input
            className="bg-white border-black border block p-1"
            placeholder="email"
            {...register("email")}
          />
          <AnimatePresence>
            {otpSent && (
              <motion.div
                initial={{ y: "-100%" }}
                animate={{ y: 0 }}
                transition={{ bounce: 0, duration: 0.1 }}
                className="grid place-items-center gap-4"
              >
                <input
                  className="bg-white border-black border block pl-1 self-baseline"
                  placeholder="one time password"
                  {...register("otp")}
                />
                <p>We've sent a one-time password to your email</p>
              </motion.div>
            )}
          </AnimatePresence>
          {formState.errors.email && formState.touchedFields.email && (
            <ErrorMessage>{formState.errors.email.message}</ErrorMessage>
          )}
          {formState.errors.email && (
            <ErrorMessage>{formState.errors.email.message}</ErrorMessage>
          )}
          {otpError && <ErrorMessage>{otpError.message}</ErrorMessage>}
          {loginMutation.error && (
            <ErrorMessage>{getErrorMessage(loginMutation.error)}</ErrorMessage>
          )}
          <button className="bg-black text-white px-4 py-2 rounded-none">
            Submit
          </button>
          {/* <p className="text-center my-4">or</p> */}
          {/* <button */}
          {/*   className="disabled:cursor-not-allowed bg-orange-2 text-white px-4 py-2" */}
          {/*   type="button" */}
          {/*   onClick={async () => { */}
          {/*     setShowTurnstile(true); */}
          {/*   }} */}
          {/* > */}
          {/*   Try For Free! */}
          {/* </button> */}
          {showTurnstile && (
            <Turnstile
              real={import.meta.env.MODE !== "test"}
              sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY as string}
              onVerify={async (token) => {
                await signupAnon(token);
                window.location.href = import.meta.env.VITE_TINKER_URL;
              }}
              onError={(err) => {
                console.error(err);
              }}
            />
          )}
          {isPending && (
            <p className="w-full text-wrap break-words flex-grow-0">{tb}...</p>
          )}
        </form>
      </main>
    </div>
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    try {
      const parsed = JSON.parse(error.message);
      return parsed.message || error.message;
    } catch {
      return error.message;
    }
  } else if (typeof error === "string") {
    return error;
  } else if (error && typeof error === "object" && "message" in error) {
    return (error as { message: string }).message;
  }
  return "An unknown error occurred";
}

export function Header() {
  return (
    <header
      className={
        "items-center justify-between px-4 py-8 bg-white text-black border-b-black border-b flex"
      }
    >
      <div className="flex items-center text-lg">
        <Link to="/" className="mr-4 my-auto flex items-center h-[32px]">
          <Logo className="h-full aspect-square" />
        </Link>
        <Link to="/">
          <li className="underline-effect my-auto text-3xl font-heading">
            Tinker
          </li>
        </Link>
      </div>
      <Spacer />
      <div className="relative flex items-end gap-4 h-full">
        <div
          className="my-auto font-body"
          data-for="mode"
          data-tip="Light/Dark"
        ></div>
        <div className="flex items-center h-full ">
          <Link to="/mcp" className="mx-4">MCP</Link>
          <a
            href={import.meta.env.VITE_DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className="mx-4"
            data-for="docs"
            data-tip="Docs"
          >
            Docs
          </a>
        </div>
      </div>
    </header>
  );
}
